/**
 * Brauz "Book a Stylist" SDK loader.
 *
 * The SDK is never rendered from theme.liquid — it loads on demand from whichever
 * surface needs it, so pages without a Brauz trigger ship none of it. The load
 * promise is module scope, so any number of triggers on a page share a single
 * fetch and a single init call.
 */

const SDK_SRC =
  'https://d3aq2u4yw77ivo.cloudfront.net/retailer-appointment/public/1.0/bookAStylist.prod.js'

export interface BrauzCustomer {
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
}

declare global {
  interface Window {
    Brauz?: {
      initializeBrauzBookAStylist?: (accountId: string) => void
    }
    Brauz_open_book_a_stylist_dialog?: () => void
    Brauz_book_a_stylist_customer_data?: BrauzCustomer
  }
}

let loadPromise: Promise<void> | null = null

/**
 * Inject and initialise the SDK. Safe to call repeatedly — the first call does the
 * work and later calls receive the same promise. A failed load clears the cache so
 * a subsequent call retries.
 */
export function loadBrauz(accountId: string, customer?: BrauzCustomer): Promise<void> {
  if (loadPromise) return loadPromise
  if (!accountId) return Promise.reject(new Error('Brauz: missing account ID'))

  const promise = new Promise<void>((resolve, reject) => {
    // Assign before init so the SDK cannot read an empty prefill on startup.
    if (customer) window.Brauz_book_a_stylist_customer_data = customer

    const script = document.createElement('script')
    script.src = SDK_SRC
    script.async = true

    script.onload = () => {
      const brauz = window.Brauz
      if (typeof brauz?.initializeBrauzBookAStylist !== 'function') {
        reject(new Error('Brauz: SDK loaded without initializeBrauzBookAStylist'))
        return
      }
      brauz.initializeBrauzBookAStylist(accountId)
      resolve()
    }
    script.onerror = () => reject(new Error('Brauz: SDK failed to load'))

    document.head.appendChild(script)
  })

  loadPromise = promise
  // Clear the cache on failure so a later click can retry, without clobbering a
  // retry that is already in flight.
  promise.catch(() => {
    if (loadPromise === promise) loadPromise = null
  })

  return promise
}

/** Load the SDK if needed, then open the booking dialog. */
export async function openBookAStylist(
  accountId: string,
  customer?: BrauzCustomer
): Promise<void> {
  await loadBrauz(accountId, customer)
  window.Brauz_open_book_a_stylist_dialog?.()
}
